import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../api';
import { ModernProgressRing } from '../../ui/modern';
import { useIsMobile } from '../../../hooks/useIsMobile';

/**
 * Progress Summary Component
 * Displays progress rings showing branch onboarding progress
 *
 * Granular progress:
 * - Users: per-enabled-service field completion (Yes or N/A counts as done)
 * - Devices: per-field completion for device quick fields (Yes or N/A counts as done)
 */
function entityDataToValues(data) {
  const values = {};
  (data || []).forEach((item) => {
    if (item.valueText != null) values[item.fieldKey] = item.valueText;
    else if (item.valueNumber != null) values[item.fieldKey] = item.valueNumber;
    else if (item.valueBoolean != null) values[item.fieldKey] = item.valueBoolean;
    else if (item.valueDate != null) values[item.fieldKey] = item.valueDate;
    else if (item.valueJson != null) values[item.fieldKey] = item.valueJson;
  });
  return values;
}

function isValueDone(value) {
  if (value === true) return true;
  const v = String(value || '').trim();
  return v === 'Yes' || v === 'N/A';
}

function pct(done, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

const ProgressSummary = ({ branchId, sectorId, allowedUserFieldKeys = [] }) => {
  const isMobile = useIsMobile();
  const ringSize = isMobile ? 120 : 160;
  const strokeWidth = isMobile ? 10 : 12;

  const [loading, setLoading] = useState(true);
  const [userEntity, setUserEntity] = useState({ fields: [], instances: [] });
  const [deviceEntity, setDeviceEntity] = useState({ fields: [], instances: [] });

  useEffect(() => {
    let cancelled = false;

    async function loadEntity(entityTypeCode) {
      if (!sectorId || !branchId) return { fields: [], instances: [] };
      const typeRes = await api.get('/entity-types', {
        params: { sectorId, code: entityTypeCode },
      });
      const entityType = (typeRes.data || [])[0];
      if (!entityType?.id) return { fields: [], instances: [] };

      const [fieldsRes, instancesRes] = await Promise.all([
        api.get(`/entity-types/${entityType.id}/fields`),
        api.get('/entity-instances', { params: { entityTypeId: entityType.id, branchId } }),
      ]);

      return { fields: fieldsRes.data || [], instances: instancesRes.data || [] };
    }

    async function run() {
      if (!sectorId || !branchId) return;
      setLoading(true);
      try {
        const [users, devices] = await Promise.all([
          loadEntity('branch_user'),
          loadEntity('branch_device'),
        ]);
        if (!cancelled) {
          setUserEntity(users);
          setDeviceEntity(devices);
        }
      } catch (e) {
        console.error('progress summary load', e);
        if (!cancelled) {
          setUserEntity({ fields: [], instances: [] });
          setDeviceEntity({ fields: [], instances: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [branchId, sectorId]);

  const stats = useMemo(() => {
    const userQuickFields = (userEntity.fields || [])
      .filter((f) => f.group === 'quick' && f.isActive !== false)
      .map((f) => f.fieldKey);

    const allowedUserSet = new Set(Array.isArray(allowedUserFieldKeys) ? allowedUserFieldKeys : []);
    const requiredUserFieldKeys = userQuickFields.filter((k) => allowedUserSet.has(k));

    let userTotal = 0;
    let userDone = 0;
    (userEntity.instances || []).forEach((inst) => {
      const values = entityDataToValues(inst.EntityData || []);
      requiredUserFieldKeys.forEach((fieldKey) => {
        userTotal += 1;
        if (isValueDone(values[fieldKey])) userDone += 1;
      });
    });

    const deviceQuickFieldKeys = (deviceEntity.fields || [])
      .filter((f) => f.group === 'quick' && f.isActive !== false)
      .map((f) => f.fieldKey);

    let deviceTotal = 0;
    let deviceDone = 0;
    (deviceEntity.instances || []).forEach((inst) => {
      const values = entityDataToValues(inst.EntityData || []);
      deviceQuickFieldKeys.forEach((fieldKey) => {
        deviceTotal += 1;
        if (isValueDone(values[fieldKey])) deviceDone += 1;
      });
    });

    return {
      overall: { done: userDone + deviceDone, total: userTotal + deviceTotal },
      users: { done: userDone, total: userTotal },
      devices: { done: deviceDone, total: deviceTotal },
    };
  }, [userEntity.fields, userEntity.instances, deviceEntity.fields, deviceEntity.instances, allowedUserFieldKeys]);

  return (
    <div className="flex flex-wrap gap-6 mb-8">
      <div className="flex flex-col items-center">
        <ModernProgressRing
          percentage={loading ? 0 : pct(stats.overall.done, stats.overall.total)}
          size={ringSize}
          strokeWidth={strokeWidth}
          label="Overall"
        />
        {!loading && (
          <div className="mt-1 text-xs text-slate-400">
            {stats.overall.done}/{stats.overall.total} tasks
          </div>
        )}
      </div>

      <div className="flex flex-col items-center">
        <ModernProgressRing
          percentage={loading ? 0 : pct(stats.users.done, stats.users.total)}
          size={ringSize}
          strokeWidth={strokeWidth}
          label="Users"
        />
        {!loading && (
          <div className="mt-1 text-xs text-slate-400">
            {stats.users.done}/{stats.users.total} tasks
          </div>
        )}
      </div>

      <div className="flex flex-col items-center">
        <ModernProgressRing
          percentage={loading ? 0 : pct(stats.devices.done, stats.devices.total)}
          size={ringSize}
          strokeWidth={strokeWidth}
          label="Devices"
        />
        {!loading && (
          <div className="mt-1 text-xs text-slate-400">
            {stats.devices.done}/{stats.devices.total} tasks
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressSummary;

